/* =====================================================================
 * DocumentEditor — 기존 document 모드 전용 래퍼
 *
 * 내부 편집 엔진은 공용 BlockEditor(src/components/editors/BlockEditor)
 * 에 위임하고, 제목(TitleArea) 영역과 documentStore 저장만 담당한다.
 * ===================================================================== */

import React, { useCallback, useEffect, useRef } from 'react';
import type { JSONContent } from '@tiptap/react';
import { useDocumentStore } from '@auw/stores';
import { BlockEditor } from '../editors/BlockEditor';
import { EditorWrap, TitleArea } from './editor.styles';

/* ------------------------------------------------------------------ */
/* Props                                                               */
/* ------------------------------------------------------------------ */

interface DocumentEditorProps {
  documentId: string;
  initialContent: JSONContent;
  onTitleChange?: (title: string) => void;
  title?: string;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const DocumentEditor: React.FC<DocumentEditorProps> = ({
  documentId,
  initialContent,
  onTitleChange,
  title = '',
}) => {
  const updateContent = useDocumentStore((s) => s.updateContent);
  const titleRef = useRef<HTMLTextAreaElement>(null);

  const handleChange = useCallback(
    (value: JSONContent) => {
      updateContent(documentId, value);
    },
    [documentId, updateContent],
  );

  /* ── 제목 자동 높이 ── */
  const handleTitleInput = useCallback(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    handleTitleInput();
  }, [title, handleTitleInput]);

  return (
    <EditorWrap>
      <TitleArea
        ref={titleRef}
        value={title}
        onChange={(e) => onTitleChange?.(e.target.value)}
        onInput={handleTitleInput}
        placeholder="제목 없는 문서"
        rows={1}
      />
      <BlockEditor
        value={initialContent}
        onChange={handleChange}
        contentKey={documentId}
        debounceMs={500}
      />
    </EditorWrap>
  );
};
